import { Header, RankingItem, Tabs, Text, Select } from 'components';
import {
  queryKeys,
  routes,
  SESSION_KEYS,
  TEXT_STRING,
  TOKYO_AREA_ID,
} from '../../constants';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ERankingTime, ERankingSearchParams, ERankingCategory } from 'types';
import { useQuery } from '@tanstack/react-query';
import { getFemaleRanking } from 'api';
import { parseJSON } from 'utils';
import { useAppStore } from 'store';

const RANKING_STRINGS = TEXT_STRING.ranking;
const COMMON_STRINGS = TEXT_STRING.common;

const categoryOptions = [
  {
    title: RANKING_STRINGS.overallRating,
    id: ERankingCategory.OVERALL_RATING,
  },
  {
    title: RANKING_STRINGS.numSetSoloProject,
    id: ERankingCategory.SET_COUNT,
  },
  {
    title: RANKING_STRINGS.giftCount,
    id: ERankingCategory.GIFT_COUNT,
  },
];

const periodOptions = [
  {
    title: RANKING_STRINGS.timePeriod.yesterday,
    id: ERankingTime.YESTERDAY,
  },
  {
    title: RANKING_STRINGS.timePeriod.last7Days,
    id: ERankingTime.LAST_7_DAYS,
  },
  {
    title: RANKING_STRINGS.timePeriod.last30Days,
    id: ERankingTime.LAST_30_DAYS,
  },
];

function Ranking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { franchiseAreas } = useAppStore();

  const [selectedCategory, selectedPeriod, selectedArea] = useMemo(() => {
    return [
      searchParams.get(ERankingSearchParams.CATEGORY) ||
        ERankingCategory.OVERALL_RATING,
      searchParams.get(ERankingSearchParams.PERIOD) || ERankingTime.YESTERDAY,
      parseInt(
        searchParams.get(ERankingSearchParams.AREA_ID) || String(TOKYO_AREA_ID)
      ),
    ];
  }, [searchParams]);

  const {
    data: listFemaleRanking,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [queryKeys.RANKING, selectedArea],
    queryFn: () => getFemaleRanking({ areaId: selectedArea }),
    enabled: !!selectedArea && !!franchiseAreas?.length,
  });

  const listView = useMemo(() => {
    if (!listFemaleRanking) {
      return [];
    }

    const timeData = listFemaleRanking[selectedPeriod as ERankingTime];

    if (!timeData) {
      return [];
    }

    const categoryData = (timeData as any)[selectedCategory];

    const adjustedRankingData = (categoryData || []).map(
      (item: any, index: number) => {
        return {
          ...item,
          ranking: index + 1,
        };
      }
    );

    return adjustedRankingData;
  }, [listFemaleRanking, selectedPeriod, selectedCategory]);

  const handleChangeParam = useCallback(
    (key: string, value: string | number) => {
      const currentParams = Object.fromEntries(searchParams.entries());
      const dataUpdate = { ...currentParams, [key]: String(value) };
      sessionStorage.setItem(SESSION_KEYS.RANKING, JSON.stringify(dataUpdate));
      setSearchParams(dataUpdate);
    },
    [searchParams, setSearchParams]
  );

  const handleCategoryChange = useCallback(
    (category: string) => {
      handleChangeParam(ERankingSearchParams.CATEGORY, category);
    },
    [handleChangeParam]
  );

  const handlePeriodChange = useCallback(
    (period: string) => {
      handleChangeParam(ERankingSearchParams.PERIOD, period);
    },
    [handleChangeParam]
  );

  const handleAreaChange = useCallback(
    (area: string) => {
      handleChangeParam(ERankingSearchParams.AREA_ID, parseInt(area));
    },
    [handleChangeParam]
  );

  const areaOptions = useMemo(
    () =>
      franchiseAreas
        ?.filter((area) => !area.isOtherArea)
        ?.map((area) => ({
          name: area.name,
          value: area.id.toString(),
        })) || [],
    [franchiseAreas]
  );

  useEffect(() => {
    const sessionTab = parseJSON(sessionStorage.getItem(SESSION_KEYS.RANKING));
    if (
      sessionTab &&
      !searchParams.get(ERankingSearchParams.CATEGORY) &&
      !searchParams.get(ERankingSearchParams.PERIOD) &&
      !searchParams.get(ERankingSearchParams.AREA_ID)
    ) {
      setSearchParams(sessionTab);
    } else if (franchiseAreas && franchiseAreas.length) {
      const needsUpdate =
        !searchParams.get(ERankingSearchParams.CATEGORY) ||
        !searchParams.get(ERankingSearchParams.PERIOD) ||
        !searchParams.get(ERankingSearchParams.AREA_ID);

      if (needsUpdate) {
        const currentParams = Object.fromEntries(searchParams.entries());
        const dataUpdate = {
          ...currentParams,
          [ERankingSearchParams.CATEGORY]:
            searchParams.get(ERankingSearchParams.CATEGORY) ||
            ERankingCategory.OVERALL_RATING,
          [ERankingSearchParams.PERIOD]:
            searchParams.get(ERankingSearchParams.PERIOD) ||
            ERankingTime.YESTERDAY,
          [ERankingSearchParams.AREA_ID]:
            searchParams.get(ERankingSearchParams.AREA_ID) ||
            String(TOKYO_AREA_ID),
        };
        setSearchParams(dataUpdate, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, franchiseAreas]);

  // 初回アクセス時にデフォルトクエリパラメータを設定
  useEffect(() => {
    if (franchiseAreas && franchiseAreas.length) {
      const hasAnyParams = searchParams.toString().length;
      if (!hasAnyParams) {
        setSearchParams(
          {
            category: ERankingCategory.OVERALL_RATING,
            period: ERankingTime.YESTERDAY,
            areaId: String(TOKYO_AREA_ID),
          },
          { replace: true }
        );
      }
    }
  }, [franchiseAreas, searchParams, setSearchParams]);

  return (
    <div className="h-full overflow-y-auto">
      <Header
        title={RANKING_STRINGS.ratingRanking}
        onBack={() => navigate(routes.SEARCH)}
      />

      <div className="px-4 pt-4">
        <Tabs
          currentTab={selectedCategory}
          tabs={categoryOptions}
          onChangeTab={handleCategoryChange}
        />
      </div>

      <div className="px-4 pt-4">
        <Tabs
          currentTab={selectedPeriod}
          tabs={periodOptions}
          onChangeTab={handlePeriodChange}
        />
      </div>

      <div className="px-4 pt-4">
        <Select
          name="area"
          label="エリア"
          value={String(selectedArea)}
          onChange={handleAreaChange}
          options={areaOptions}
          className="mb-4"
        />
      </div>
      <div className="flex flex-col gap-4 px-4 py-4">
        {isLoading || isRefetching ? (
          <>
            {Array.from(Array(5).keys())?.map((index) => {
              return (
                <div className="p-4 flex items-center gap-5" key={index}>
                  <div className="w-12 h-8 bg-neutral-300 animate-pulse"></div>
                  <div className="rounded-full w-12 aspect-square bg-neutral-300"></div>
                  <div className="flex-1 h-8 bg-neutral-300 animate-pulse"></div>
                </div>
              );
            })}
          </>
        ) : listView?.length ? (
          listView.map((data: any, index: number) =>
            data.users.map((user: any) => (
              <RankingItem
                rank={data.ranking}
                value={data.value}
                unit=""
                user={user}
                key={index}
              />
            ))
          )
        ) : (
          <Text bold center fontSize={18} className="mt-8">
            {COMMON_STRINGS.noData}
          </Text>
        )}
      </div>
    </div>
  );
}

export default Ranking;
